<template>
    <ValidationObserver ref="workRegistration" v-slot="{ passes }" >
        <BaseDialog
            :showDialog="showDialog"
            maxDialogWidth="1000"
            maxCardHeight="1000"
            headline="lang_PUBLISHING_WORKS_ACTION_OPTION_WORKS_REGISTRATION"
            :close="closeDialog"
            :alvqa="alvqa"
        >

            <template v-slot:dialogText>

                <!-- TARGET selector for Work Registration -->
                <v-container fluid>
                    <v-row>
                        <v-col cols="12">
                            <BaseEditPageDropdown
                                :context="context"
                                field="Target"
                                :items="getWorksRegistrationTargets"
                                :label="$t('lang_PUBLISHING_WORKS_CAPTION_WORKS_REGISTRATION_TARGETS')"
                                :selection="true"
                                :withMargin="false"
                                hideDetails
                                :clearable="true"
                                rules="required"
                                :additionalFunction="switchTarget"
                                :alvqa="alvqa + '-dropdown-works-registration-targets'"
                                :disabled="deactivatedForm"
                            />
                        </v-col>
                    </v-row>
                </v-container>

                <!-- CWR Work Registration -->
                <v-container v-if="isCWR">
                    <v-row>
                        <v-col cols="12">
                            <BaseEditPageDropdown
                                :context="context"
                                :field="'CwrSubmitter'"
                                :items="CwrSubmitterItems"
                                :label="$t('lang_CONTRACTS_PUBLISHING_WORKSEXPORT_CWR_SUBMITTER')"
                                :selection="true"
                                :withMargin="false"
                                :clearable="false"
                                :alvqa="alvqa + '-cwr-dropdown-submitter'"
                                rules="required|workRegistrationCwrSubmitterCode"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <BaseEditPageDropdown
                                :context="context"
                                :field="'ShareType'"
                                :items="ShareTypeItems"
                                :label="$t('lang_PUBLISHING_CAPTION_SHARE_TYPE')"
                                :selection="true"
                                :withMargin="false"
                                hideDetails
                                valueOuter="shareTypeOuter"
                                :additionalFunction="switchListOfPublishers"
                                :clearable="false"
                                :alvqa="alvqa + '-dropdown-share-type'"
                                rules="required"
                            />
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12">
                            <BaseEditPageDropdown
                                :context="context"
                                :field="'InterestedParties'"
                                :items="interestedPartiesItems"
                                :label="$t('lang_PUBLISHING_WORKS_CAPTION_ADD_SE_AND_COLL')"
                                :selection="true"
                                :withMargin="false"
                                hideDetails
                                :clearable="false"
                                :alvqa="alvqa + '-dropdown-interested-parties'"
                                rules="required"
                            />
                        </v-col>
                    </v-row>
                    <v-row no-gutters class="mt-4">
                        <v-col cols="12">
                            <v-btn
                                text
                                small
                                class="px-1"
                                @click="showAdvancedOptions = !showAdvancedOptions"
                                :alvqa="alvqa + '-toggle-advanced-options'"
                            >
                                {{ showAdvancedOptions ? $t('lang_GENERIC_CAPTION_HIDE_ADVANCED_OPTIONS') :$t('lang_GENERIC_CAPTION_SHOW_ADVANCED_OPTIONS') }}
                                <v-icon
                                    right
                                    class="advanced-chevron"
                                    :class="{ 'rotate-180': showAdvancedOptions }"
                                >
                                    expand_more
                                </v-icon>
                            </v-btn>
                        </v-col>
                    </v-row>

                    <v-expand-transition>
                        <div v-show="showAdvancedOptions">
                            <v-row>
                                <v-col cols="12">
                                    <BaseEditPageDropdown
                                        :context="context"
                                        :field="'Pmr'"
                                        :items="getDropdownYesNo"
                                        :label="$t('lang_CONTRACTS_PUBLISHING_WORKSEXPORT_PMR')"
                                        :selection="true"
                                        :withMargin="false"
                                        hideDetails
                                        :clearable="false"
                                        :alvqa="alvqa + '-dropdown-pmr'"
                                        rules="required"
                                    />
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12">
                                    <BaseEditPageDropdown
                                        :context="context"
                                        :field="'SelectedPublisherEntry'"
                                        :items="exportPublisherEntries"
                                        :label="$t('lang_PUBLISHING_WORKS_EXPORT_DYNAMIC_PUBLISHER_AFFILIATION')"
                                        :selection="true"
                                        :withMargin="false"
                                        hideDetails
                                    />
                                </v-col>
                            </v-row>
                        </div>
                    </v-expand-transition>
                </v-container>

                <v-container fluid>
                    <!-- STEPS 1, 2 and 3 = Login, Preflight and Sending -->
                    <v-row>
                        <v-spacer />
                        <v-col cols="12" class="text-right">
                            <!-- ERROR Text-->
                            <span
                                v-if="errorText !== null"
                                style="color:brown;"
                                v-html="errorText"
                            ></span>
                            <!-- Preflight error csv -->
                            <span>
                                <BaseIcon
                                    v-if="preflightCsvPath !== null"
                                    @click.stop="downloadFile(preflightCsvPath)"
                                    icon="cloud_download"
                                    :tooltipText="$t('lang_GENERAL_CAPTION_ICON_DOWNLOAD')"
                                    :alvqa="alvqa + '-icon-download-preflight-csv'"
                                />
                            </span>
                            <!-- registration error csv -->
                            <span>
                                <BaseIcon
                                    v-if="registrationErrorCsvPath !== null"
                                    @click.stop="downloadFile(registrationErrorCsvPath)"
                                    icon="cloud_download"
                                    :tooltipText="$t('lang_GENERAL_CAPTION_ICON_DOWNLOAD')"
                                    :alvqa="alvqa + '-icon-download-registrtion-error-csv'"
                                />
                            </span>
                            <!-- SUCCESS Text -->
                            <span
                                v-if="successText !== null"
                                v-html="successText"
                            ></span>
                            <!-- XML File -->
                            <span>
                                <BaseIcon
                                    v-if="xmlFilePath !== null"
                                    @click.stop="downloadFile(xmlFilePath)"
                                    icon="cloud_download"
                                    :tooltipText="$t('lang_GENERAL_CAPTION_ICON_DOWNLOAD')"
                                    :alvqa="alvqa + '-icon-download-preflight-xml'"
                                />
                            </span>
                            <!-- Registration csv -->
                            <span>
                                <BaseIcon
                                    v-if="registrationCsvPath !== null"
                                    @click.stop="downloadFile(registrationCsvPath)"
                                    icon="cloud_download"
                                    :tooltipText="$t('lang_GENERAL_CAPTION_ICON_DOWNLOAD')"
                                    :alvqa="alvqa + '-icon-download-registration-csv'"
                                />
                            </span>

                            {{ errorText !== null || successText !== null ? ' | ' : '' }}

                            <!-- NEXT STEP Text -->
                            <span style="padding-right: 20px;" v-html="stepText"></span>

                            <!-- LOGIN BUTTON -->
                            <BaseButton
                                v-if="showLoginButton"
                                :disabled="isLoginButtonDisabled"
                                @click="passes(login)"
                                label="lang_LOGIN_BUTTON_CAPTION_LOGIN"
                                :alvqa="alvqa+'-button-gema-iwa-login'"
                            />

                            <!-- PREFLIGHT BUTTON -->
                            <BaseButton
                                v-if="showPreflightButton"
                                :disabled="isPreflightButtonDisabled"
                                @click="passes(preflight)"
                                label="lang_PUBLISHING_WORKS_WORK_REGISTRATION_CAPTION_PREFLIGHT"
                                :alvqa="alvqa+'-button-preflight'"
                            />

                            <!-- SEND BUTTON -->
                            <BaseButton
                                v-if="showSendButton"
                                :disabled="isSendButtonDisabled"
                                @click="startWorksRegistration"
                                label="lang_PUBLISHING_WORKS_WORK_REGISTRATION_CAPTION_SEND"
                                :alvqa="alvqa+'-button-start-works-registration'"
                            />
                        </v-col>
                    </v-row>
                </v-container>
            </template>

        </BaseDialog>
    </ValidationObserver>
</template>

<script>
import BaseDialog from '@/components/_base/BaseDialog'
import BaseButton from '@/components/_buttons/BaseButton'
import BaseIcon from '@/components/_buttons/BaseIcon'
import BaseEditPageDropdown from '@/components/_editForm/BaseEditPageDropdown'

import {    DropdownPublishingWorksRegistrationShareTypes,
            DropdownGenericYesNo,
            DropdownGenericYesNoOpwSpw,
 } from '@/assets/dropdowns/alvDropdowns.js'

import api from '@/api_alv'

export default {
    name: "WorksRegistrationDialog",
    components: {
        BaseDialog,
        BaseButton,
        BaseIcon,
        BaseEditPageDropdown,
    },
    data() {
        return {
            context: "WorksRegistration",
            alvqa: 'publishing-works-action-option-works-registration-dialog',
            showAdvancedOptions: false,
        }
    },
    computed: {
        showDialog: {
            get() { return this.$store.getters.getShowWorksRegistrationDialog },
            set(show) { this.$store.commit('setShowWorksRegistrationDialog', show) }
        },
        type() { return this.$store.getters.getWorksRegistrationDetailsTarget },
        exportPublisherEntries() {
            return this.$store.getters.getExportPublisherEntries || [];
        },

        interestedPartiesItems() {
            let options = DropdownGenericYesNoOpwSpw

            let svWorkRegistrationModule = this.$store.getters.getUserModules.sv_work_registration
            if (svWorkRegistrationModule.active !== 'yes') {
                options = options.filter((el) => el.value == 'no')
            }
            
            return options
            // return [
            //     {text: this.$t('lang_WORKSEXPORT_IP_WITHOUT_SE'), value: 'without-se', alvqa: this.alvqa + '-dropdown-interested-parties-without-se'},
            //     {text: this.$t('lang_WORKSEXPORT_IP_WITH_SE'), value: 'with-se', alvqa: this.alvqa + '-dropdown-interested-parties-with-se'}
            // ]
        },

        CwrSubmitterItems() {
            let companies = this.$store.getters.getPublishingCompanies
            let items = []

            companies.forEach(company => {
                // Only include companies with a CWR Submitter Code entered
                if (company.cwr && company.cwr.trim() !== '') {
                    items.push(
                        {
                            text: company.name,
                            value: company.cwr+'#'+company.id,
                            alvqa: this.alvqa + '-cwr-dropdown-submitter-' + company.name.toLowerCase().replace(/ /g,"-")
                        }
                    )
                }
            });
            if (items.length != 0)
                this.$store.commit('setExportWorksDetailsCwrSubmitter', items[0].value)
            return items
        },

        ShareTypeItems()  { 
            return DropdownPublishingWorksRegistrationShareTypes 
        },

        isCWR() { return (this.$store.getters.getWorksRegistrationDetailsTarget.slice(-3) == 'Cwr' ) },

        getFirstSelectedWork() {
            if (this.$store.getters.getSelectedWorks.length === 0 && this.$store.getters.getExportAllWorks === true && this.$store.getters.getWorks.length > 0) {
                return this.$store.getters.getWorks[0]
            } else if (this.$store.getters.getSelectedWorks.length > 0 && this.$store.getters.getWorks.length > 0) {
                let firstWorkId = this.$store.getters.getSelectedWorks[0]
                return this.$store.getters.getWorks.find(work=>work.alvId===firstWorkId)
            }

            return null
        },
        stepText() { return this.$store.getters.getWorksRegistrationDialogStepText },
        errorText() { return this.$store.getters.getWorksRegistrationDialogErrorText },
        successText() { return this.$store.getters.getWorksRegistrationDialogSuccessText },
        showLoginButton() { return this.$store.getters.getWorksRegistrationDialogShowLoginButton },
        showPreflightButton() { return this.$store.getters.getWorksRegistrationDialogShowPreflightButton },
        showSendButton() { return this.$store.getters.getworksRegistrationDialogShowSendButton },
        isLoginButtonDisabled() { return this.$store.getters.getWorksRegistrationDialogDisableLoginButton || !this.hasCwrSubmitterCode},
        isPreflightButtonDisabled() { return this.$store.getters.getWorksRegistrationDialogDisablePreflightButton },
        isSendButtonDisabled() { return Boolean(this.$store.getters.getWorksRegistrationDialogDisableSendButton)},
        deactivatedForm() { return this.$store.getters.getWorksRegistrationDeaktivateForm },
        xmlFilePath() { return this.$store.getters.getWorksRegistrationXmlFilePath },
        preflightCsvPath() { return this.$store.getters.getWorksRegistrationPreflightPathCsv},
        registrationErrorCsvPath() { return this.$store.getters.getWorksRegistrationRegistrationErrorCsv },
        registrationCsvPath() { return this.$store.getters.getWorksRegistrationRegistrationCsv },


        getWorksRegistrationTargets() {
            return this.$store.getters.getCwrSocietiesList.map(society => ({
                text: society.societyAcronym,
                value: society.societyActionPrefix,
                alvqa: this.alvqa + '-dropdown-works-registration-targets-' + society.societyAcronym.toLowerCase(),
            }))
        },
        getDropdownYesNo() { return DropdownGenericYesNo },
        hasCwrSubmitterCode() {
            let submitter = this.$store.getters.getWorksRegistrationDetailsCwrSubmitter
            if (!submitter) return false
            let parts = submitter.split("#")
            if (parts.length == 1 || parts[0].length !== 3) {
                return false
            }
            return true
        }
    },
    methods: {
        closeDialog() {
            this.showDialog = false
            this.$store.commit('setWorksRegistrationDialogDisableLoginButton', false)
            this.$store.commit('setWorksRegistrationDialogShowPreflightButton', false)
            this.$store.commit('setWorksRegistrationDialogDisablePreflightButton', false)
            this.$store.commit('setWorksRegistrationDialogShowLoginButton', true)
            this.$store.commit('setWorksRegistrationDialogShowSendButton', false)
            this.$store.commit('setWorksRegistrationDialogSuccessText', null)
            this.$store.commit('setWorksRegistrationDialogErrorText', null)
            this.$store.commit('setWorksRegistrationDialogStepText', this.$t('lang_PUBLISHING_WORKS_WORK_REGISTRATION_STEP_1_OF_3'))
            this.$store.commit('setWorksRegistrationDeaktivateForm', false)
            this.$store.commit('setWorksRegistrationXmlFilePath', null)
            this.$store.commit('setWorksRegistrationPreflightPathCsv', null)
            this.$store.commit('setWorksRegistrationRegistrationErrorCsv', null)
            this.$store.commit('setWorksRegistrationRegistrationCsv', null)
            this.$store.commit('setSelectedWorksAction', [])
            this.$store.commit('setWorksRegistrationDetailsTarget', '')
            this.$store.commit('setWorksRegistrationDetailsInterestedParties', 'no')
            this.showAdvancedOptions = false
            this.$refs.workRegistration.reset()

        },

        switchTarget() {
            const target = this.$store.getters.getWorksRegistrationDetailsTarget
            const society = this.$store.getters.getCwrSocietiesList.find(s => s.societyActionPrefix === target)
            this.$store.commit('setWorksRegistrationDetailsCwrRecipient', society ? society.societyCode : null)
        },

        switchListOfPublishers(shareType) {
            this.$store.commit('setExportWorksDetailsSelectedPublisherEntry', null)
            this.$store.commit('setExportWorksDetailsShareType', shareType)
            this.$store.dispatch('fetchExportPublisherEntries');
        },

        login() {
            this.$store.commit('setWorksRegistrationDialogDisableLoginButton', true)
            this.$store.dispatch('workRegistrationLogin')
        },
        preflight() {
            this.$store.commit('setWorksRegistrationDialogDisablePreflightButton', true)
            this.$store.dispatch('workRegistrationPreflight')
        },
        startWorksRegistration() {
            this.$store.commit('setWorksRegistrationDialogDisableSendButton', true)
            this.$store.dispatch('startWorksRegistration')
        },
        downloadFile(path) {
            api.getFileFromALV(path)
        },
        preselectCompanyDropdown() {
            if (this.CwrSubmitterItems.length === 1) {
                this.$store.commit('setWorksRegistrationDetailsCwrSubmitter', this.CwrSubmitterItems[0].value)
            }
        },
        preselectSEAndCollDropdown() {
            let firstSelectedWork = this.getFirstSelectedWork
            if (firstSelectedWork && firstSelectedWork.type === 'spw') {
                this.$store.commit('setWorksRegistrationDetailsInterestedParties', 'yes')
            }
        }
    },
    watch: {
        showDialog: function (newShowDialogValue) {
            if(newShowDialogValue) { // When dialog opens, preselect dropdowns for SE and Coll in work registration
                this.preselectSEAndCollDropdown()
                this.$store.dispatch('fetchCwrSocietiesList')
            }
        }
    },
    created() {
        this.$store.commit('setSocieties')
        this.$store.commit('setCountries')
        this.preselectCompanyDropdown()
    }
}

</script>

<style scoped>
.advanced-chevron {
    transition: transform 0.2s ease;
}
.rotate-180 {
    transform: rotate(180deg);
}
</style>
